/* eslint-disable quotes */
/* eslint-disable indent */
/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useRef } from "react";
import {
  Button,
  Input,
  message,
  Spin,
  DatePicker,
  Card,
  Select,
  Col,
  Row,
  Tag,
} from "antd";
import { SearchOutlined, ClearOutlined } from "@ant-design/icons";
import API from "../../../api";
import urls from "../../../api/urls";
import TableContainer from "../../../components/TableContainer";
import Smcs_Loader from "../../../assests/images/loader-new.gif";
import { dateFormat_dmy } from "../../../utils";
import moment from "moment";
import Heading from "../../../components/Heading";

const { CP_EXCEPTION_UPDATE_REQUESTS } = urls;
const { RangePicker } = DatePicker;

const STATUS_OPTIONS = [
  { label: "Requested", value: "requested" },
  { label: "Approved", value: "approved" },
  { label: "Rejected", value: "rejected" },
];

const getStatusColor = (status) => {
  switch (status?.toLowerCase()) {
    case "approved":
      return "green";
    case "rejected":
      return "red";
    case "requested":
      return "orange";
    default:
      return "default";
  }
};

const ExceptionRequests = () => {
  const userinfo = JSON.parse(localStorage.getItem("userinfo"));
  const cpId = userinfo?.spId;
  const debounceRef = useRef({});

  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const [dateRange, setDateRange] = useState({
    startDate: moment().format("YYYY-MM-DD"),
    endDate: moment().format("YYYY-MM-DD"),
  });
  const [awbNumber, setAwbNumber] = useState("");
  const [drsNumber, setDrsNumber] = useState("");
  const [statusFilter, setStatusFilter] = useState(null);

  const buildQueryParams = (
    page,
    limit,
    overrides = {},
  ) => {
    const merged = {
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      awb: awbNumber,
      drs: drsNumber,
      status: statusFilter,
      ...overrides,
    };
    const params = new URLSearchParams({
      cpId,
      page: page.toString(),
      limit: limit.toString(),
    });
    if (merged.startDate) params.set("fromDate", merged.startDate);
    if (merged.endDate) params.set("toDate", merged.endDate);
    if (merged.awb?.trim()) params.set("awbNumber", merged.awb.trim());
    if (merged.drs?.trim()) params.set("drsNumber", merged.drs.trim());
    if (merged.status) params.set("exceptionRequestStatus", merged.status);
    return params;
  };

  const fetchData = async (page = 1, limit = pageSize, overrides = {}) => {
    if (!cpId) {
      message.error("CP ID not found");
      return;
    }
    setLoading(true);
    try {
      const params = buildQueryParams(page, limit, overrides);
      const response = await API.Api(
        `${CP_EXCEPTION_UPDATE_REQUESTS}?${params.toString()}`,
      ).get();

      const data = response?.response?.data;
      if (data?.status === 200) {
        setTableData(data.data?.requests || []);
        setTotal(data.pagination?.totalCount || 0);
        setCurrentPage(page);
      } else {
        message.error(data?.message || "Failed to fetch requests");
        setTableData([]);
        setTotal(0);
      }
    } catch (error) {
      message.error(error?.message || "Error fetching requests");
      setTableData([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(1, pageSize);
  }, []);

  useEffect(() => {
    return () => {
      Object.values(debounceRef.current).forEach((t) => t && clearTimeout(t));
    };
  }, []);

  const handleDateChange = (dates) => {
    if (!dates) {
      const cleared = { startDate: null, endDate: null };
      setDateRange(cleared);
      fetchData(1, pageSize, { startDate: null, endDate: null });
      return;
    }
    if (dates[0] && dates[1]) {
      const newRange = {
        startDate: dates[0].format("YYYY-MM-DD"),
        endDate: dates[1].format("YYYY-MM-DD"),
      };
      setDateRange(newRange);
      setCurrentPage(1);
      fetchData(1, pageSize, newRange);
    }
  };

  const handleInputSearch = (field, value) => {
    if (field === "awbNumber") setAwbNumber(value);
    else setDrsNumber(value);

    if (debounceRef.current[field]) clearTimeout(debounceRef.current[field]);

    if (value === "" || value.length >= 3) {
      debounceRef.current[field] = setTimeout(() => {
        setCurrentPage(1);
        fetchData(1, pageSize, {
          awb: field === "awbNumber" ? value : awbNumber,
          drs: field === "drsNumber" ? value : drsNumber,
        });
      }, 500);
    }
  };

  const handleStatusChange = (value) => {
    setStatusFilter(value || null);
    setCurrentPage(1);
    fetchData(1, pageSize, { status: value || null });
  };

  const handleClearFilters = () => {
    Object.values(debounceRef.current).forEach((t) => t && clearTimeout(t));
    debounceRef.current = {};

    const today = moment().format("YYYY-MM-DD");
    setAwbNumber("");
    setDrsNumber("");
    setStatusFilter(null);
    setDateRange({ startDate: today, endDate: today });
    setCurrentPage(1);
    fetchData(1, pageSize, {
      startDate: today,
      endDate: today,
      awb: "",
      drs: "",
      status: null,
    });
  };

  const handlePageChange = (pagination) => {
    setPageSize(pagination.pageSize);
    setCurrentPage(pagination.current);
    fetchData(pagination.current, pagination.pageSize);
  };

  const activeFilterCount = [
    awbNumber.trim() !== "",
    drsNumber.trim() !== "",
    statusFilter !== null,
  ].filter(Boolean).length;

  const hasActiveFilters = activeFilterCount > 0;

  const columns = [
    {
      title: "S.No",
      key: "sNo",
      width: 60,
      render: (_, __, index) => (currentPage - 1) * pageSize + index + 1,
    },
    {
      title: "AWB Number",
      dataIndex: "awbNumber",
      key: "awbNumber",
      render: (text) => text || "N/A",
    },
    {
      title: "DRS Number",
      dataIndex: "drsNumber",
      key: "drsNumber",
      render: (text) => text || "N/A",
    },
    {
      title: "Requested To",
      dataIndex: "requestedTo",
      key: "requestedTo",
      render: (text) => text || "N/A",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (text) => (
        <Tag color={getStatusColor(text)}>
          {text?.toUpperCase() || "N/A"}
        </Tag>
      ),
    },
    {
      title: "Request Raised For",
      dataIndex: "reqRaisedFor",
      key: "reqRaisedFor",
      render: (text) => text || "N/A",
    },
    {
      title: "Reason",
      dataIndex: "reason",
      key: "reason",
      render: (text) => text || "N/A",
    },
    {
      title: "Remarks",
      dataIndex: "remarks",
      key: "remarks",
      render: (text) => text || "N/A",
    },
    {
      title: "Requested Date",
      dataIndex: "requestedDate",
      key: "requestedDate",
      width: 160,
      render: (text) =>
        text ? moment(text).format(dateFormat_dmy + " hh:mm A") : "N/A",
    },
  ];

  return (
    <Spin
      spinning={loading}
      indicator={
        <img src={Smcs_Loader} style={{ height: "100px", width: "100px" }} />
      }
    >
      <div className="master-main-container m-1 mt-3 p-1">
        <Heading title="Update Status Requests" navigateTo="/drs" />

        <Card style={{ marginTop: "24px" }}>
          {/* Filters Row */}
          <Row align="top" gutter={[16, 8]} style={{ marginBottom: "4px" }}>
            <Col>
              <label>Date Range:</label>
              <div style={{ marginTop: "4px" }}>
                <RangePicker
                  value={
                    dateRange.startDate && dateRange.endDate
                      ? [
                          moment(dateRange.startDate, "YYYY-MM-DD"),
                          moment(dateRange.endDate, "YYYY-MM-DD"),
                        ]
                      : null
                  }
                  format={dateFormat_dmy}
                  onChange={handleDateChange}
                  disabledDate={(current) =>
                    current && current > moment().endOf("day")
                  }
                  className="input-field-style"
                  style={{ width: "225px" }}
                />
              </div>
            </Col>
            <Col>
              <label>AWB Number:</label>
              <div style={{ marginTop: "4px" }}>
                <Input
                  placeholder="Search AWB number"
                  prefix={<SearchOutlined />}
                  value={awbNumber}
                  onChange={(e) =>
                    handleInputSearch("awbNumber", e.target.value)
                  }
                  allowClear
                  className="input-field-style"
                  style={{ width: 200 }}
                />
              </div>
            </Col>
            <Col>
              <label>DRS Number:</label>
              <div style={{ marginTop: "4px" }}>
                <Input
                  placeholder="Search DRS number"
                  prefix={<SearchOutlined />}
                  value={drsNumber}
                  onChange={(e) =>
                    handleInputSearch("drsNumber", e.target.value)
                  }
                  allowClear
                  className="input-field-style"
                  style={{ width: 200 }}
                />
              </div>
            </Col>
            <Col>
              <label>Status:</label>
              <div style={{ marginTop: "4px" }}>
                <Select
                  placeholder="All statuses"
                  value={statusFilter}
                  onChange={handleStatusChange}
                  options={STATUS_OPTIONS}
                  allowClear
                  style={{ width: 160, height: "40px" }}
                />
              </div>
            </Col>
          </Row>

          {/* Clear Filters Row */}
          <Row justify="end" align="middle" className="mb-1 mr-1">
            <Col>
              <Button
                type="default"
                icon={<ClearOutlined />}
                onClick={handleClearFilters}
                disabled={!hasActiveFilters}
              >
                Clear Filters{" "}
                {activeFilterCount > 0 && `(${activeFilterCount})`}
              </Button>
            </Col>
          </Row>

          <TableContainer
            columns={columns}
            dataSource={tableData}
            rowKey={(_, index) => index}
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: total,
              showSizeChanger: true,
              pageSizeOptions: ["10", "20", "50"],
              showTotal: (t, range) => `${range[0]}-${range[1]} of ${t} items`,
            }}
            onChange={handlePageChange}
          />
        </Card>
      </div>
    </Spin>
  );
};

export default ExceptionRequests;
